import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import { entityListKey, type EntityListKey } from '../../../shared/api/entityQueryKeys.js';

export interface NoteMemberRef {
  id: string;
  userId: string;
  user: { id: string; name: string; email: string };
}

export type NoteVisibility = 'workspace' | 'own' | 'shared';

/** Одна привязка заметки: заполнено ровно одно поле сущности. */
export interface NoteLinkItem {
  id: string;
  dealId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  taskId?: string | null;
  calendarEventId?: string | null;
  documentId?: string | null;
  templateId?: string | null;
  projectId?: string | null;
  deal?: { id: string; title: string } | null;
  company?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName?: string | null } | null;
  task?: { id: string; title: string } | null;
  calendarEvent?: { id: string; title: string } | null;
  document?: { id: string; title: string } | null;
  template?: { id: string; name: string } | null;
  project?: { id: string; name: string } | null;
}

export interface NoteItem {
  id: string;
  workspaceId: string;
  body: string;
  visibility: NoteVisibility;
  createdAt: string;
  updatedAt: string;
  createdByMember?: NoteMemberRef | null;
  links: NoteLinkItem[];
  /** Точечные доступы: участники, с которыми заметка поделена. */
  shares: Array<{ memberId: string }>;
}

export interface NoteLinkInput {
  dealId?: string;
  companyId?: string;
  contactId?: string;
  taskId?: string;
  calendarEventId?: string;
  documentId?: string;
  templateId?: string;
  projectId?: string;
}

export interface ListNotesFilters {
  q?: string;
  dealId?: string;
  companyId?: string;
  contactId?: string;
  taskId?: string;
  calendarEventId?: string;
  documentId?: string;
  templateId?: string;
  projectId?: string;
  limit?: number;
}

export interface CreateNoteInput {
  body: string;
  links?: NoteLinkInput[];
  visibility?: NoteVisibility;
}

export interface UpdateNoteInput {
  body?: string;
  /** Передан — заменяет набор привязок целиком (пустой массив = отвязать всё). */
  links?: NoteLinkInput[];
  visibility?: NoteVisibility;
  /** Передан — заменяет набор точечных доступов целиком. */
  sharedWithMemberIds?: string[];
}

export function getNotesQueryKey(
  workspaceId: string,
  filters: ListNotesFilters = {},
): EntityListKey {
  return entityListKey('notes', workspaceId, { ...filters });
}

export async function listNotes(filters: ListNotesFilters = {}): Promise<NoteItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) search.set('q', filters.q.trim());
  if (filters.dealId) search.set('dealId', filters.dealId);
  if (filters.companyId) search.set('companyId', filters.companyId);
  if (filters.contactId) search.set('contactId', filters.contactId);
  if (filters.taskId) search.set('taskId', filters.taskId);
  if (filters.calendarEventId) search.set('calendarEventId', filters.calendarEventId);
  if (filters.documentId) search.set('documentId', filters.documentId);
  if (filters.templateId) search.set('templateId', filters.templateId);
  if (filters.projectId) search.set('projectId', filters.projectId);
  if (filters.limit !== undefined) search.set('limit', String(filters.limit));
  const path = search.size > 0 ? `/notes?${search.toString()}` : '/notes';
  return apiGet<NoteItem[]>(path);
}

export async function createNote(input: CreateNoteInput): Promise<NoteItem> {
  return apiPost<NoteItem, CreateNoteInput>('/notes', input);
}

export async function updateNote(id: string, input: UpdateNoteInput): Promise<NoteItem> {
  return apiPatch<NoteItem, UpdateNoteInput>(`/notes/${id}`, input);
}

export async function deleteNote(id: string): Promise<void> {
  await apiDelete<void>(`/notes/${id}`);
}

/** Точечный доступ: выдать заметку участнику (только автор). */
export async function shareNote(id: string, memberId: string): Promise<NoteItem> {
  return apiPost<NoteItem, { memberId: string }>(`/notes/${id}/share`, { memberId });
}

/** Точечный доступ: снять выдачу (только автор). */
export async function unshareNote(id: string, memberId: string): Promise<NoteItem> {
  return apiDelete<NoteItem>(`/notes/${id}/share/${encodeURIComponent(memberId)}`);
}
